// cdc-custom-grant.interceptor.ts
import { Injectable, Inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { CDC_CONFIDENTIAL, CdcConfidentialConfig } from './cdc-confidential.config';

@Injectable()
export class CdcCustomGrantInterceptor implements HttpInterceptor {
  private tokenPath = '/authorizationserver/oauth/token';

  constructor(@Inject(CDC_CONFIDENTIAL) private conf: CdcConfidentialConfig) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    try {
      if (req.method !== 'POST') return next.handle(req);
      if (!this.isTokenEndpoint(req.url)) return next.handle(req);
      if (!this.isFormUrlEncoded(req.headers)) return next.handle(req);

      const bodyMap = this.toFormMap(req.body);
      if (!bodyMap) return next.handle(req);

      const grantType = (bodyMap.get('grant_type') || '').trim();
      const clientId  = (bodyMap.get('client_id')  || '').trim();

      // 🚀 Extend: rewrite when PUBLIC client is used for EITHER 'custom' OR 'client_credentials'
      const needsRewrite =
        clientId === 'mobile_android_public' &&
        (grantType === 'custom' || grantType === 'client_credentials');

      if (needsRewrite) {
        console.debug('[CDC Interceptor] Rewriting token request to confidential client for grant:', grantType);

        bodyMap.set('client_id', this.conf.clientId);          // -> 'mobile_android'
        bodyMap.set('client_secret', this.conf.clientSecret);  // -> 'secret'

        // NOTE: We keep body-based client_secret because your backend accepts it that way (matches your “expected” payload).
        // If you prefer HTTP Basic, see the alternative snippet below.

        const newBody = this.fromFormMap(bodyMap);
        const newHeaders = req.headers.set('Content-Type', 'application/x-www-form-urlencoded');

        const cloned = req.clone({ body: newBody, headers: newHeaders });
        return next.handle(cloned);
      }
    } catch (e) {
      console.warn('[CDC Interceptor] Skipped due to error:', e);
    }
    return next.handle(req);
  }

  private isTokenEndpoint(url: string): boolean {
    if (this.conf.tokenUrlBase) {
      return url.startsWith(this.conf.tokenUrlBase) && url.includes(this.tokenPath);
    }
    return url.includes(this.tokenPath);
  }

  private isFormUrlEncoded(headers: HttpHeaders): boolean {
    const ct = (headers.get('Content-Type') || '').toLowerCase();
    return ct.includes('application/x-www-form-urlencoded') || ct === '';
  }

  private toFormMap(body: any): Map<string, string> | null {
    if (body == null) return null;
    if (typeof body === 'string') return this.parseForm(body);
    if (body instanceof HttpParams) return this.parseForm(body.toString());
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return this.parseForm(body.toString());
    }
    if (typeof body.toString === 'function') return this.parseForm(body.toString());
    return null;
  }

  private parseForm(encoded: string): Map<string, string> {
    const map = new Map<string, string>();
    if (!encoded) return map;
    encoded.split('&').forEach(pair => {
      const [k, v] = pair.split('=');
      map.set(decodeURIComponent(k || ''), decodeURIComponent(v || ''));
    });
    return map;
  }

  private fromFormMap(map: Map<string, string>): string {
    const parts: string[] = [];
    map.forEach((v, k) => parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`));
    return parts.join('&');
  }
}
